/* eslint-disable @typescript-eslint/naming-convention */
import { DeviceStatus as deviceStatus } from 'src/types/device_status';
import getSubstationDanfossA332_4StatusByKey from 'src/utils/getSubstationDanfossA332_4StatusByKey';
import getSubstationDanfossA230_2StatusByKey from 'src/utils/getSubstationDanfossA230_2StatusByKey';
import getSubstationDanfossA230_4StatusByKey from 'src/utils/getSubstationDanfossA230_4StatusByKey';
import getSubstationDanfossA333_3StatusByKey from 'src/utils/getSubstationDanfossA333_3StatusByKey';

// Canonical variant keys. These double as the manufacturer_variant value stored
// on the device and as the property name used in export/bulk register payloads.
export const SUBS_ECL_310_A332_4 = 'SUBS_ECL_310_A332_4';
export const SUBS_ECL_310_A230_2 = 'SUBS_ECL_310_A230_2';
export const SUBS_ECL_310_A230_4 = 'SUBS_ECL_310_A230_4';
export const SUBS_ECL_310_A333_3 = 'SUBS_ECL_310_A333_3';

export const SUBSTATION_DANFOSS_VARIANTS = [
  SUBS_ECL_310_A332_4,
  SUBS_ECL_310_A230_2,
  SUBS_ECL_310_A230_4,
  SUBS_ECL_310_A333_3,
];

// Human-readable names for the variant keys stored on the device. The stored
// key stays the filter/write value; only the displayed text comes from here.
export const SUBSTATION_DANFOSS_VARIANT_LABELS = {
  [SUBS_ECL_310_A332_4]: 'ECL 310 A332.4',
  [SUBS_ECL_310_A230_2]: 'ECL 310 A230.2',
  [SUBS_ECL_310_A230_4]: 'ECL 310 A230.4',
  [SUBS_ECL_310_A333_3]: 'ECL 310 A333.3',
};

export const getSubstationDanfossVariantLabel = (variant: any): string => (
  SUBSTATION_DANFOSS_VARIANT_LABELS[variant] ?? variant
);

// Reverse of the above: the filter input holds the readable name, but the query
// has to run against the key stored on the device.
export const getSubstationDanfossVariantKey = (label: any): any => (
  SUBSTATION_DANFOSS_VARIANTS.find(
    (variant) => SUBSTATION_DANFOSS_VARIANT_LABELS[variant] === label
  ) ?? label
);

const STATUS_BY_VARIANT = {
  [SUBS_ECL_310_A332_4]: getSubstationDanfossA332_4StatusByKey,
  [SUBS_ECL_310_A230_2]: getSubstationDanfossA230_2StatusByKey,
  [SUBS_ECL_310_A230_4]: getSubstationDanfossA230_4StatusByKey,
  [SUBS_ECL_310_A333_3]: getSubstationDanfossA333_3StatusByKey,
};

// Values written by older builds of the device edit form, plus the loose forms
// (`ecl 310 a332.4`, `ECL_310_A332_4`) that reach us from imports.
const ALIASES = {
  ECL_310_A3324: SUBS_ECL_310_A332_4,
  ECL_310_A332_4: SUBS_ECL_310_A332_4,
  SUBS_ECL_310_A232_4: SUBS_ECL_310_A332_4,
  ECL_310_A2302: SUBS_ECL_310_A230_2,
  ECL_310_A230_2: SUBS_ECL_310_A230_2,
  ECL_310_A2304: SUBS_ECL_310_A230_4,
  ECL_310_A230_4: SUBS_ECL_310_A230_4,
  ECL_310_A3333: SUBS_ECL_310_A333_3,
  ECL_310_A333_3: SUBS_ECL_310_A333_3,
  // Retired variant, dropped from the dropdown and from export payloads.
  // Devices saved before the ECL 310 split still carry it and resolve to the
  // A332.4 register set, which is what they were always served.
  ECL_310_A232: SUBS_ECL_310_A332_4,
  SUBS_ECL_310_A232: SUBS_ECL_310_A332_4,
};

// Normalises a raw manufacturer_variant into a canonical variant key.
// Returns null when the device is not a Danfoss substation.
export const getSubstationDanfossVariant = (manufacturerVariant: any): string => {
  if (!manufacturerVariant) return null;

  const normalised = String(manufacturerVariant).trim().toUpperCase().replace(/[\s.-]/g, '_');
  if (STATUS_BY_VARIANT[normalised]) return normalised;
  return ALIASES[normalised] || null;
};

export const isSubstationDanfossDevice = (device: any): boolean => (
  !!getSubstationDanfossVariant(device?.manufacturer_variant)
);

// Register definitions for a device's variant, falling back to A332.4 so a
// device with an unrecognised variant still renders its registers.
export const getSubstationDanfossStatusForDevice = (
  device: any,
  status_key: string,
  translator: any = {},
): Array<deviceStatus> => {
  const variant = getSubstationDanfossVariant(device?.manufacturer_variant);
  const loader = STATUS_BY_VARIANT[variant] || getSubstationDanfossA332_4StatusByKey;
  return loader(status_key, translator);
};

export type SubCat = { statusKey: string; category: string; titleLocaleKey: string };
export const SUBSTATION_CATEGORY_MAP: Record<string, SubCat[]> = {
  substation_general: [
    { statusKey: 'modbus_substation_danfoss_operating_state', category: 'general_1', titleLocaleKey: 'substation_menu_general_1' },
    { statusKey: 'modbus_substation_danfoss_operating_state', category: 'general_2', titleLocaleKey: 'substation_menu_general_2' },
    { statusKey: 'modbus_substation_danfoss_operating_state', category: 'general_3', titleLocaleKey: 'substation_menu_general_3' },
    { statusKey: 'modbus_substation_danfoss_operating_state', category: 'general_4', titleLocaleKey: 'substation_menu_general_4' },
    { statusKey: 'modbus_substation_danfoss_operating_state', category: 'general_5', titleLocaleKey: 'substation_menu_general_5' },
    { statusKey: 'modbus_substation_danfoss_operating_state', category: 'general_6', titleLocaleKey: 'substation_menu_general_6' },
  ],
  substation_application: [
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'application_1', titleLocaleKey: 'substation_menu_application_1' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'application_2', titleLocaleKey: 'substation_menu_application_2' },
  ],
  substation_ecl: [
    { statusKey: 'modbus_substation_danfoss_operating_state', category: 'ecl_alarm', titleLocaleKey: 'substation_menu_ecl_alarm' },
    { statusKey: 'modbus_substation_danfoss_operating_state', category: 'ecl_system', titleLocaleKey: 'substation_menu_ecl_system' },
    { statusKey: 'modbus_substation_danfoss_operating_state', category: 'ecl_system_ex', titleLocaleKey: 'substation_menu_ecl_system_ex' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'ecl_system', titleLocaleKey: 'substation_menu_ecl_system_meas' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'ecl_extension', titleLocaleKey: 'substation_menu_ecl_extension' },
  ],
  substation_return_limit: [
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'return_limit_1', titleLocaleKey: 'substation_menu_return_limit_1' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'return_limit_2', titleLocaleKey: 'substation_menu_return_limit_2' },
  ],
  substation_output: [
    { statusKey: 'modbus_substation_danfoss_operating_state', category: 'output_status', titleLocaleKey: 'substation_menu_output_status' },
    { statusKey: 'modbus_substation_danfoss_operating_state', category: 'output_manual_status', titleLocaleKey: 'substation_menu_output_manual_status' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'output_control_1', titleLocaleKey: 'substation_menu_output_control_1' },
  ],
  substation_flow_temperature: [
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'flow_temperature_1', titleLocaleKey: 'substation_menu_flow_temperature_1' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'flow_temperature_2', titleLocaleKey: 'substation_menu_flow_temperature_2' },
  ],
  substation_raw_sensor: [{ statusKey: 'modbus_substation_danfoss_operating_state', category: 'raw_sensor', titleLocaleKey: 'substation_menu_raw_sensor' }],
  substation_input_config: [{ statusKey: 'modbus_substation_danfoss_operating_state', category: 'input_config', titleLocaleKey: 'substation_menu_input_config' }],
  substation_optimization_1: [{ statusKey: 'modbus_substation_danfoss_measurements', category: 'optimization_1', titleLocaleKey: 'substation_menu_optimization_1' }],
  substation_control_parameter_1: [
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'control_parameter_1', titleLocaleKey: 'substation_menu_control_parameter_1' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'control_parameter_2', titleLocaleKey: 'substation_menu_control_parameter_2' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'control_parameter_5', titleLocaleKey: 'substation_menu_control_parameter_5' },
  ],
  substation_temp_monitoring_2: [
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'temp_monitoring_1', titleLocaleKey: 'substation_menu_temp_monitoring_1' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'temp_monitoring_2', titleLocaleKey: 'substation_menu_temp_monitoring_2' },
  ],
  substation_compensating1_2: [
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'compensating1_1', titleLocaleKey: 'substation_menu_compensating1_1' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'compensating1_2', titleLocaleKey: 'substation_menu_compensating1_2' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'compensating2_1', titleLocaleKey: 'substation_menu_compensating2_1' },
  ],
  substation_flow_power_limit: [{ statusKey: 'modbus_substation_danfoss_measurements', category: 'flow_power_limit_1', titleLocaleKey: 'substation_menu_flow_power_limit_1' }],
  substation_pressure: [
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'pressure1', titleLocaleKey: 'substation_menu_pressure1' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'pressure2', titleLocaleKey: 'substation_menu_pressure2' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'pressure3', titleLocaleKey: 'substation_menu_pressure3' },
    { statusKey: 'modbus_substation_danfoss_measurements', category: 'pressure4', titleLocaleKey: 'substation_menu_pressure4' },
  ],
  substation_alarm1_1: [{ statusKey: 'modbus_substation_danfoss_measurements', category: 'alarm1_1', titleLocaleKey: 'substation_menu_alarm1_1' }],
  substation_energy_meter: [{ statusKey: 'modbus_substation_danfoss_measurements', category: 'energy_meter_1', titleLocaleKey: 'substation_menu_energy_meter_1' }],
  substation_ethernet: [{ statusKey: 'modbus_substation_danfoss_measurements', category: 'ethernet', titleLocaleKey: 'substation_menu_ethernet' }],
  substation_scada_override: [{ statusKey: 'modbus_substation_danfoss_measurements', category: 'scada_override', titleLocaleKey: 'substation_menu_scada_override' }],
  substation_m_bus_config: [{ statusKey: 'modbus_substation_danfoss_measurements', category: 'm_bus_config', titleLocaleKey: 'substation_menu_m_bus_config' }],
};

// The register files call translator() for enum rows, so callers that only need
// register numbers or categories must still pass something callable — the
// default `{}` parameter would throw "translator is not a function".
const noopTranslator = (): string => '';

// How many registers a variant actually exposes per substation tab. Tabs that
// come back 0 hold no registers for that controller and are hidden from the
// menu rather than rendering as an empty panel.
export const getSubstationDanfossTabCounts = (variant: string): Record<string, number> => {
  const loader = STATUS_BY_VARIANT[variant] || getSubstationDanfossA332_4StatusByKey;
  const byCategory = {};

  ['modbus_substation_danfoss_operating_state', 'modbus_substation_danfoss_measurements']
    .forEach((statusKey) => {
      (loader(statusKey, noopTranslator) || []).forEach((status: any) => {
        const id = `${statusKey}|${status?.category}`;
        byCategory[id] = (byCategory[id] || 0) + 1;
      });
    });

  return Object.keys(SUBSTATION_CATEGORY_MAP).reduce((acc, tabKey) => ({
    ...acc,
    [tabKey]: SUBSTATION_CATEGORY_MAP[tabKey]
      .reduce((sum, c) => sum + (byCategory[`${c.statusKey}|${c.category}`] || 0), 0),
  }), {});
};

// The categories behind one substation tab that actually hold registers for a
// variant. Categories a controller does not expose are dropped so the status
// page never renders an empty table.
export const getSubstationDanfossTabCategories = (
  variant: string,
  tabKey: string,
): Array<SubCat> => {
  const loader = STATUS_BY_VARIANT[variant] || getSubstationDanfossA332_4StatusByKey;
  const populated = new Set();

  ['modbus_substation_danfoss_operating_state', 'modbus_substation_danfoss_measurements']
    .forEach((statusKey) => {
      (loader(statusKey, noopTranslator) || []).forEach((status: any) => {
        populated.add(`${statusKey}|${status?.category}`);
      });
    });

  return (SUBSTATION_CATEGORY_MAP[tabKey] || [])
    .filter((c) => populated.has(`${c.statusKey}|${c.category}`));
};

// Register numbers behind one substation tab for a given variant, derived from
// that variant's own definitions so each controller fetches only what it has.
export const getSubstationDanfossTabRegisters = (
  variant: string,
  tabKey: string,
): Array<number> => {
  const cats = SUBSTATION_CATEGORY_MAP[tabKey] || SUBSTATION_CATEGORY_MAP.substation_general;
  const loader = STATUS_BY_VARIANT[variant] || getSubstationDanfossA332_4StatusByKey;
  const wanted = new Set(cats.map((c) => `${c.statusKey}|${c.category}`));

  return ['modbus_substation_danfoss_operating_state', 'modbus_substation_danfoss_measurements']
    .reduce((acc, statusKey) => acc.concat(
      loader(statusKey, noopTranslator)
        ?.filter((status: any) => wanted.has(`${statusKey}|${status?.category}`))
        ?.map((status: any) => parseInt(status?.nr, 10)) || []
    ), [])
    .filter((nr) => !Number.isNaN(nr));
};

// Register definitions for an already-resolved canonical variant key. Falls
// back to A332.4 so an unknown key still yields a usable register set.
export const getSubstationDanfossStatusForVariant = (
  variant: string,
  status_key: string,
  translator: any = {},
): Array<deviceStatus> => {
  const loader = STATUS_BY_VARIANT[variant] || getSubstationDanfossA332_4StatusByKey;
  return loader(status_key, translator);
};

// Union of the register definitions across every variant, de-duplicated by
// register number. Used by list/export screens, which build one column set for
// a mixed list of devices rather than for a single device's variant.
export const getSubstationDanfossStatusForAllVariants = (
  status_key: string,
  translator: any = {},
): Array<deviceStatus> => {
  const seen = new Set();
  // A332.4 first so its long-standing definitions win on any overlap.
  const loaders = [
    getSubstationDanfossA332_4StatusByKey,
    getSubstationDanfossA230_2StatusByKey,
    getSubstationDanfossA230_4StatusByKey,
    getSubstationDanfossA333_3StatusByKey,
  ];

  return loaders.reduce((combined, loader) => {
    const unseen = loader(status_key, translator)?.filter((status) => {
      if (seen.has(status?.nr)) return false;
      seen.add(status?.nr);
      return true;
    });
    return combined.concat(unseen || []);
  }, []);
};

export default getSubstationDanfossVariant;
